/**
 * Export Map CSV Parser Utilities
 *
 * Functions for parsing and generating export map CSV files.
 * CSV format: LABEL, TYPE, VARIABLE, VALUES
 */

import {
  ExportMapVariable,
  ExportMapCategory,
  ExportMapVariableType,
  ExportMapGroup,
} from "src/types/exportMap.types"

/**
 * Splits raw CSV content into logical records, honoring newlines that appear
 * inside quoted fields.
 *
 * The engine emits multi-line values (e.g. question text) inside quoted columns,
 * so a single record can span several physical lines. Splitting naively on
 * `\r?\n` would break such a record into fragments — producing phantom variable
 * rows whose codes are mangled fragments of the quoted text. This walks the
 * content tracking quote state and only breaks on a newline when not inside a
 * quoted field.
 *
 * @param csvContent - Raw CSV string
 * @returns Array of record strings (blank records omitted)
 */
export const splitCSVRecords = (csvContent: string): string[] => {
  const records: string[] = []
  let current = ""
  let inQuotes = false

  for (let i = 0; i < csvContent.length; i++) {
    const char = csvContent[i]

    if (char === '"') {
      // Preserve escaped quotes ("") so the pair doesn't flip quote state
      if (inQuotes && csvContent[i + 1] === '"') {
        current += '""'
        i++
        continue
      }
      inQuotes = !inQuotes
      current += char
    } else if ((char === "\n" || char === "\r") && !inQuotes) {
      // Record boundary; collapse \r\n into a single break
      if (char === "\r" && csvContent[i + 1] === "\n") i++
      if (current.trim()) records.push(current)
      current = ""
    } else {
      current += char
    }
  }

  if (current.trim()) records.push(current)
  return records
}

/**
 * Parses a CSV string into rows, handling quoted values correctly
 */
export const parseCSVLine = (line: string): string[] => {
  const result: string[] = []
  let current = ""
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        // Escaped quote
        current += '"'
        i++
      } else {
        // Toggle quote mode
        inQuotes = !inQuotes
      }
    } else if (char === "," && !inQuotes) {
      result.push(current.trim())
      current = ""
    } else {
      current += char
    }
  }

  result.push(current.trim())
  return result
}

/**
 * Escapes a value for CSV format
 */
const escapeCSVValue = (value: string): string => {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

/**
 * Parses the VALUES data into category objects
 * Input can be either:
 * - An array of strings (from already-parsed CSV row)
 * - A comma-separated string
 *
 * Supports format: code1,label1,code2,label2,... (alternating code,label pairs)
 */
const parseCategories = (
  values: string[] | string | undefined,
): ExportMapCategory[] | undefined => {
  if (!values) return undefined

  // Convert string to array if needed (for backwards compatibility)
  let parts: string[]
  if (typeof values === "string") {
    if (values.trim() === "") return undefined
    parts = values.split(",").map((p) => p.trim())
  } else {
    parts = values.map((p) => p.trim())
  }

  if (parts.length === 0) return undefined

  const categories: ExportMapCategory[] = []

  // Check if using label:code format.
  //
  // EVERY part must look like `<label>:<number>`. Testing whether *any* part
  // contains a colon is not sufficient: a colon is legal inside a category
  // label (e.g. "Provide email:"), and treating one such label as a format
  // marker silently discards every part without a colon — collapsing the whole
  // category set to a single entry, coded 0 (because Number("") === 0).
  //
  // Split on the LAST colon so labels may themselves contain colons.
  const isLabelCodeFormat = parts.every((p) => {
    const idx = p.lastIndexOf(":")
    if (idx <= 0 || idx === p.length - 1) return false
    const codeStr = p.slice(idx + 1).trim()
    return codeStr !== "" && !isNaN(Number(codeStr))
  })

  if (isLabelCodeFormat) {
    for (const part of parts) {
      const idx = part.lastIndexOf(":")
      const label = part.slice(0, idx).trim()
      const code = Number(part.slice(idx + 1).trim())
      categories.push({ label, code })
    }
    return categories.length > 0 ? categories : undefined
  }

  // Check if all parts are numeric (codes only format)
  const allNumeric = parts.every((p) => !isNaN(Number(p)))
  if (allNumeric && parts.length > 0) {
    // Format: just codes like "1,2,3,4,5"
    // Use code as both label and code
    for (const codeStr of parts) {
      const code = Number(codeStr)
      categories.push({ label: String(code), code })
    }
    return categories.length > 0 ? categories : undefined
  }

  // Default format: code,label pairs (alternating)
  // e.g., "1,Advertising & Marketing,2,Media or market research"
  for (let i = 0; i < parts.length; i += 2) {
    const codeStr = parts[i]
    const label = parts[i + 1] || codeStr // Fallback to code if no label

    const code = isNaN(Number(codeStr)) ? codeStr : Number(codeStr)
    categories.push({ label, code })
  }

  return categories.length > 0 ? categories : undefined
}

/**
 * Converts categories to VALUES column format
 * Output format: "code1,label1,code2,label2,..."
 * e.g., "1,Male,2,Female,3,Non-binary"
 * Labels containing commas are quoted: 1,Yes,2,"No, I'm waiting"
 */
const categoriesToString = (
  categories: ExportMapCategory[] | undefined,
): string => {
  if (!categories || categories.length === 0) return ""
  return categories
    .map((cat) => {
      // Quote label if it contains comma or quotes
      const label =
        cat.label.includes(",") || cat.label.includes('"')
          ? `"${cat.label.replace(/"/g, '""')}"`
          : cat.label
      return `${cat.code},${label}`
    })
    .join(",")
}

/**
 * Parses export map CSV content into ExportMapVariable array
 *
 * Note: The VALUES column contains comma-separated code,label pairs but is NOT quoted,
 * so we need to join everything from the VALUES column onwards.
 *
 * @param csvContent - Raw CSV string
 * @returns Array of parsed variables
 */
export const parseExportMapCSV = (csvContent: string): ExportMapVariable[] => {
  const lines = splitCSVRecords(csvContent)
  if (lines.length === 0) return []

  // Parse header to get column indices
  const header = parseCSVLine(lines[0])
  const labelIdx = header.findIndex((h) => h.toUpperCase().includes("LABEL"))
  const typeIdx = header.findIndex((h) => h.toUpperCase().includes("TYPE"))
  const variableIdx = header.findIndex((h) =>
    h.toUpperCase().includes("VARIABLE"),
  )
  const valuesIdx = header.findIndex((h) => h.toUpperCase().includes("VALUES"))
  // Optional column carrying the original question wording (engine emits it only when some row has text)
  const fieldMetadataIdx = header.findIndex(
    (h) => h.trim().toUpperCase() === "FIELD_METADATA",
  )

  if (labelIdx === -1 || typeIdx === -1 || variableIdx === -1) {
    console.warn("Export map CSV missing required columns")
    return []
  }

  const variables: ExportMapVariable[] = []

  for (let i = 1; i < lines.length; i++) {
    const row = parseCSVLine(lines[i])
    if (row.length < 3) continue

    const fullLabel = row[labelIdx] || ""
    const type = (row[typeIdx] || "TEXT").toUpperCase() as ExportMapVariableType
    const variableCode = row[variableIdx] || ""

    // VALUES column and everything after it is the category data
    // Pass as array to preserve any quoted values that contained commas
    let valuesArr: string[] | undefined
    if (valuesIdx >= 0 && row.length > valuesIdx) {
      valuesArr = row.slice(valuesIdx)
    }

    // Parse path from label (split by //)
    const path = fullLabel.split("//").map((p) => p.trim())

    const variable: ExportMapVariable = {
      path,
      fullLabel,
      type,
      variableCode,
      isNew: false, // Will be determined by merging
      categories: parseCategories(valuesArr),
    }

    if (fieldMetadataIdx >= 0) {
      const text = (row[fieldMetadataIdx] || "").trim()
      if (text) variable.fieldMetadata = text
    }

    // Max-multi: BOOL fields with a single code,label pair in VALUES
    if (type === "BOOL" && valuesArr && valuesArr.length >= 1) {
      const code = parseInt(valuesArr[0], 10)
      if (!isNaN(code)) {
        variable.maxMultiCode = code
      }
    }

    variables.push(variable)
  }

  return variables
}

/**
 * Converts ExportMapVariable array back to CSV string
 *
 * @param variables - Array of variables to convert
 * @returns CSV string
 */
export const exportMapToCSV = (variables: ExportMapVariable[]): string => {
  // FIELD_METADATA (question wording) is opt-in, mirroring the engine: only emitted
  // when at least one variable carries it, so existing 4-column profiles stay byte-identical.
  const withText = variables.some((v) => Boolean(v.fieldMetadata))
  const header = withText
    ? "LABEL,TYPE,VARIABLE,FIELD_METADATA,VALUES"
    : "LABEL,TYPE,VARIABLE,VALUES"
  const rows = variables.map((v) => {
    const label = escapeCSVValue(v.fullLabel)
    const type = v.type
    const variable = withText
      ? `${escapeCSVValue(v.variableCode)},${escapeCSVValue(v.fieldMetadata || "")}`
      : escapeCSVValue(v.variableCode)

    // Max-multi BOOL: serialize code,label into VALUES
    if (v.type === "BOOL" && v.maxMultiCode !== undefined) {
      const boolLabel = v.path[v.path.length - 1] || v.fullLabel
      return `${label},${type},${variable},${v.maxMultiCode},${escapeCSVValue(boolLabel)}`
    }

    // VALUES are NOT escaped - they spread across multiple cells (code,label,code,label,...)
    const values = categoriesToString(v.categories)
    // Only add VALUES column if there are categories
    if (values) {
      return `${label},${type},${variable},${values}`
    }
    return `${label},${type},${variable}`
  })

  return [header, ...rows].join("\n")
}

/**
 * Result of grouping variables - includes both grouped and root-level variables
 */
export interface GroupedVariablesResult {
  groups: ExportMapGroup[]
  rootVariables: ExportMapVariable[]
}

/**
 * Groups variables into a hierarchical structure based on their paths.
 * Root-level variables (without //) are returned separately to avoid unnecessary nesting.
 * Order is preserved from the original CSV.
 *
 * @param variables - Flat array of variables
 * @returns Hierarchical group structure plus root variables
 */
export const groupVariables = (
  variables: ExportMapVariable[],
): GroupedVariablesResult => {
  const rootGroups: ExportMapGroup[] = []
  const rootVariables: ExportMapVariable[] = []
  const groupMap = new Map<string, ExportMapGroup>()
  const groupOrder: string[] = [] // Track order of first appearance

  // First pass: create groups for all unique paths and collect root variables
  for (const variable of variables) {
    if (variable.path.length <= 1) {
      // Root level variable - don't create a group, add directly
      rootVariables.push(variable)
      continue
    }

    // Create groups for each level of the path (except the variable itself)
    for (let i = 0; i < variable.path.length - 1; i++) {
      const groupPath = variable.path.slice(0, i + 1)
      const groupKey = groupPath.join("//")

      if (!groupMap.has(groupKey)) {
        const group: ExportMapGroup = {
          label: groupPath[groupPath.length - 1],
          path: groupPath,
          variables: [],
          subgroups: [],
          isExpanded: false, // Collapsed by default for performance
        }
        groupMap.set(groupKey, group)
        if (i === 0) {
          groupOrder.push(groupKey) // Track order of root-level groups
        }
      }
    }
  }

  // Second pass: assign variables to their parent groups
  for (const variable of variables) {
    if (variable.path.length > 1) {
      // Nested variable - add to parent group
      const parentPath = variable.path.slice(0, -1)
      const parentKey = parentPath.join("//")
      const parentGroup = groupMap.get(parentKey)
      if (parentGroup) {
        parentGroup.variables.push(variable)
      }
    }
  }

  // Third pass: build hierarchy (preserve order)
  for (const [, group] of groupMap) {
    if (group.path.length === 1) {
      // Root level group - will be added in order below
    } else {
      // Find parent and add as subgroup
      const parentPath = group.path.slice(0, -1)
      const parentKey = parentPath.join("//")
      const parentGroup = groupMap.get(parentKey)
      if (parentGroup) {
        parentGroup.subgroups.push(group)
      }
    }
  }

  // Add root groups in original order
  for (const key of groupOrder) {
    const group = groupMap.get(key)
    if (group) {
      rootGroups.push(group)
    }
  }

  return { groups: rootGroups, rootVariables }
}

/**
 * Flattens a group hierarchy back into a flat variable array
 *
 * @param groups - Hierarchical group structure
 * @returns Flat array of variables
 */
export const flattenGroups = (groups: ExportMapGroup[]): ExportMapVariable[] => {
  const variables: ExportMapVariable[] = []

  const processGroup = (group: ExportMapGroup) => {
    variables.push(...group.variables)
    group.subgroups.forEach(processGroup)
  }

  groups.forEach(processGroup)
  return variables
}

/**
 * One entry in a rendered tree level: either a variable or a group.
 */
export type ExportMapTreeItem =
  | { kind: "variable"; variable: ExportMapVariable }
  | { kind: "group"; group: ExportMapGroup }

/**
 * Maps each variable's full label to its index in the flat (map/data) order.
 *
 * @param variables - Flat, ordered array of variables
 * @returns fullLabel -> index (first occurrence wins)
 */
export const buildPositionIndex = (
  variables: ExportMapVariable[],
): Map<string, number> => {
  const position = new Map<string, number>()
  variables.forEach((v, i) => {
    if (!position.has(v.fullLabel)) position.set(v.fullLabel, i)
  })
  return position
}

/**
 * Smallest position of any variable inside a group (recursively).
 * Infinity when none of its variables is in the index.
 */
const groupPosition = (
  group: ExportMapGroup,
  position: Map<string, number>,
): number => {
  let min = Infinity
  for (const v of group.variables) {
    const p = position.get(v.fullLabel)
    if (p !== undefined && p < min) min = p
  }
  for (const sg of group.subgroups) {
    const p = groupPosition(sg, position)
    if (p < min) min = p
  }
  return min
}

/**
 * Interleaves one tree level's variables and groups in data order, so the tree
 * reads like the questionnaire instead of "all variables, then all groups".
 * A variable sorts by its own position; a group by the smallest position of
 * any variable inside it. Unknown labels sort last; ties keep input order
 * (variables before groups).
 *
 * @param groups - Groups at this level
 * @param variables - Variables at this level
 * @param position - Index from buildPositionIndex over the flat variable list
 * @returns Variables and groups merged in data order
 */
export const orderTreeItems = (
  groups: ExportMapGroup[],
  variables: ExportMapVariable[],
  position: Map<string, number>,
): ExportMapTreeItem[] => {
  const items: { item: ExportMapTreeItem; pos: number; seq: number }[] = []
  let seq = 0
  for (const variable of variables) {
    items.push({
      item: { kind: "variable", variable },
      pos: position.get(variable.fullLabel) ?? Infinity,
      seq: seq++,
    })
  }
  for (const group of groups) {
    items.push({
      item: { kind: "group", group },
      pos: groupPosition(group, position),
      seq: seq++,
    })
  }
  items.sort((a, b) => {
    if (a.pos !== b.pos) return a.pos < b.pos ? -1 : 1
    return a.seq - b.seq
  })
  return items.map((x) => x.item)
}
